import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import PCFeedbackProductPicker from './pc';
import H5FeedbackProductPicker from './h5';

export interface FeedbackProductItem {
    id: string | number;
    name: string;
    value: string;
}

export interface FeedbackProductCategory {
    id: string | number;
    name: string;
}

export interface FetchProductsParams {
    categoryId?: string;
    keywords?: string;
}

export interface FeedbackProductPickerProps {
    visible: boolean;
    categories: FeedbackProductCategory[];
    value?: string;
    onClose: () => void;
    onConfirm: (product: FeedbackProductItem | null) => void;
    fetchProducts?: (params: FetchProductsParams) => Promise<FeedbackProductItem[]>;
}

/** 机型选择器内部状态 */
export interface ProductPickerLoaderState {
    activeCategoryId: string;
    searchValue: string;
    selectedValue: string;
    selectedProduct: FeedbackProductItem | null;
    products: FeedbackProductItem[];
    loading: boolean;
}

/** 解析 /api/v5/products/categories 分类列表 */
export const parseProductCategories = (res: any): FeedbackProductCategory[] => {
    if (!res || res.code !== 0) {
        return [];
    }

    const rawList = res?.data?.data || res?.data?.list || res?.data || [];
    if (!Array.isArray(rawList)) {
        return [];
    }

    return rawList.map((category: any, categoryIndex: number) => ({
        id: category.id ?? category.categoryId ?? categoryIndex,
        name: category.name || category.categoryName || category.title || `分类${categoryIndex + 1}`,
    })).filter((category: FeedbackProductCategory) => category.name);
};

/** 解析 /api/v5/products 产品列表（按分类筛选） */
export const parseProductList = (res: any): FeedbackProductItem[] => {
    if (!res || res.code !== 0) {
        return [];
    }

    const rawList = res?.data?.data || res?.data?.list || res?.data || [];
    if (!Array.isArray(rawList)) {
        return [];
    }

    return rawList.map((product: any, productIndex: number) => ({
        id: product.id ?? product.productId ?? productIndex,
        name: product.name || product.title || product.productName || product.modelName || `机型${productIndex + 1}`,
        value: String(product.id ?? product.productId ?? ''),
    })).filter((item: FeedbackProductItem) => item.value !== '');
};

/** 搜索接口可能忽略关键词，遍历分页后按展示名称匹配，避免漏掉后续页机型。 */
export const fetchProductOptions = async (
    fetchPage: (params: Record<string, any>) => Promise<any>,
    { categoryId, keywords }: FetchProductsParams,
): Promise<FeedbackProductItem[]> => {
    const keyword = keywords?.trim() || '';
    const products: FeedbackProductItem[] = [];
    let pageNumber = 1;
    let hasMore = false;

    do {
        const res = await fetchPage({
            ...(categoryId ? { categoryId } : {}),
            ...(keyword ? { keywords: keyword } : {}),
            'page[number]': pageNumber,
            'page[size]': 100,
        });
        if (!res || res.code !== 0) {
            throw new Error('获取机型列表失败');
        }
        products.push(...parseProductList(res));
        hasMore = !!keyword && res.data?.page?.hasMore === true;
        pageNumber += 1;
    } while (hasMore);

    const normalizedKeyword = keyword.toLowerCase();
    return keyword
        ? products.filter(product => product.name.toLowerCase().includes(normalizedKeyword))
        : products;
};

/** 根据 value 从当前产品列表中查找已选机型 */
export const findProductByValue = (
    products: FeedbackProductItem[],
    value?: string | number,
): FeedbackProductItem | null => {
    if (value === undefined || value === null || value === '') return null;
    const normalized = String(value);
    return products.find((item) => item.value === normalized) || null;
};

/** 创建选择器初始状态 */
export const createInitialPickerState = (): ProductPickerLoaderState => ({
    activeCategoryId: '',
    searchValue: '',
    selectedValue: '',
    selectedProduct: null,
    products: [],
    loading: false,
});

/** 弹框打开时的状态：默认选中第一个分类 */
export const buildPickerOpenState = (
    categories: FeedbackProductCategory[],
    value?: string,
): ProductPickerLoaderState => {
    const firstCategory = categories[0];
    const activeCategoryId = firstCategory ? String(firstCategory.id) : '';

    return {
        ...createInitialPickerState(),
        activeCategoryId,
        selectedValue: value ? String(value) : '',
    };
};

/** 是否需要在弹框内初始化/补加载第一个分类 */
export const shouldInitializePicker = (
    prevProps: Pick<FeedbackProductPickerProps, 'visible' | 'categories'>,
    nextProps: Pick<FeedbackProductPickerProps, 'visible' | 'categories'>,
    state: Pick<ProductPickerLoaderState, 'activeCategoryId'>,
): boolean => {
    if (nextProps.visible && !prevProps.visible) {
        return true;
    }
    if (!nextProps.visible || !nextProps.categories.length) {
        return false;
    }
    if (!state.activeCategoryId) {
        return true;
    }
    if (!prevProps.categories.length && nextProps.categories.length > 0) {
        return true;
    }
    return false;
};

/** 弹框内是否处于全局搜索模式 */
export const isProductSearchMode = (keyword: string) => !!keyword.trim();

/** 切换分类时拉取该分类下的产品列表 */
export const loadProductsForCategory = async (
    fetchProducts: ((params: FetchProductsParams) => Promise<FeedbackProductItem[]>) | undefined,
    categoryId: string,
): Promise<FeedbackProductItem[]> => {
    if (!fetchProducts || !categoryId) {
        return [];
    }
    return fetchProducts({ categoryId });
};

/** 全局搜索产品（不按分类） */
export const loadProductsByKeyword = async (
    fetchProducts: ((params: FetchProductsParams) => Promise<FeedbackProductItem[]>) | undefined,
    keywords: string,
): Promise<FeedbackProductItem[]> => {
    const normalized = keywords.trim();
    if (!fetchProducts || !normalized) {
        return [];
    }
    return fetchProducts({ keywords: normalized });
};

export const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

interface FeedbackProductPickerWrapperProps extends FeedbackProductPickerProps {
    app?: AppStore;
}

/** 机型选择器：PC 居中弹窗 / H5 底部抽屉，按分类动态加载产品 */
@inject('app')
@observer
class FeedbackProductPicker extends React.Component<FeedbackProductPickerWrapperProps> {
    /** 按当前平台渲染 PC 或 H5 选择器 */
    render() {
        const { app, ...props } = this.props;
        const platform = app?.platform || 'h5';
        if (platform === 'pc') {
            return <PCFeedbackProductPicker {...props} />;
        }
        return <H5FeedbackProductPicker {...props} />;
    }
}

export default FeedbackProductPicker;
